import { useEffect, useMemo, useState } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import type { GlyphColor } from '../types/glyph';
import { useApiClientsOrNull } from '../context/ApiClientsContext';

export interface SchemaMetadata {
  systemName: string;
  displayName: string;
  glyph: string | null;
  glyphColor?: GlyphColor;
  description: string | null;
}

export interface UseSchemaMetadataResult {
  /** Map keyed by schemaSystemName → metadata. Populated incrementally. */
  resolved: Map<string, SchemaMetadata>;
  loading: boolean;
}

/**
 * Format a schema system_name into a friendly fallback label.
 * Used when the schema service is unreachable or the schema doesn't exist.
 */
function formatSystemName(systemName: string): string {
  return systemName
    .replace(/^center_/, '')
    .replaceAll('_', ' ')
    .replaceAll(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Module-scope cache. Keyed by `${organizationId}::${schemaSystemName}` so
 * different orgs (or impersonations) don't trample each other's resolutions.
 */
const moduleCache = new Map<string, SchemaMetadata>();

/**
 * Resolve a list of schema system_names into `{ displayName, glyph }` so
 * UI surfaces can render `Sphere Type` chips with glyph + friendly label
 * instead of leaking raw `center_xxx` strings to users.
 *
 * The hook is incrementally consistent: it always returns a Map containing
 * a fallback (formatSystemName) entry for each requested name, then upgrades
 * entries to the real metadata once the schema-service responds. Consumers
 * can render the fallback immediately and avoid layout shift.
 */
export function useSchemaMetadata(
  systemNames: string[] | null | undefined,
): UseSchemaMetadataResult {
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const uniqueNames = useMemo(() => {
    if (!systemNames || systemNames.length === 0) return [] as string[];
    const set = new Set(systemNames.filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [systemNames]);

  const namesKey = uniqueNames.join(',');

  const [resolved, setResolved] = useState<Map<string, SchemaMetadata>>(() => {
    const initial = new Map<string, SchemaMetadata>();
    for (const name of uniqueNames) {
      const cacheKey = `${organizationId ?? ''}::${name}`;
      const cached = moduleCache.get(cacheKey);
      initial.set(
        name,
        cached ?? {
          systemName: name,
          displayName: formatSystemName(name),
          glyph: null,
          description: null,
        },
      );
    }
    return initial;
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (uniqueNames.length === 0) {
      setResolved((prev) => (prev.size === 0 ? prev : new Map()));
      setLoading(false);
      return;
    }

    // Seed resolved with cache-or-fallback for every requested name so
    // consumers never see undefined entries.
    setResolved((prev) => {
      const next = new Map(prev);
      for (const name of uniqueNames) {
        const cacheKey = `${organizationId ?? ''}::${name}`;
        const cached = moduleCache.get(cacheKey);
        if (cached) {
          next.set(name, cached);
        } else if (!next.has(name)) {
          next.set(name, {
            systemName: name,
            displayName: formatSystemName(name),
            glyph: null,
            description: null,
          });
        }
      }
      return next;
    });

    if (!apiClients?.schemaClient || !organizationId) {
      return;
    }

    const namesToFetch = uniqueNames.filter(
      (name) => !moduleCache.has(`${organizationId}::${name}`),
    );
    if (namesToFetch.length === 0) {
      return;
    }

    let cancelled = false;
    setLoading(true);

    const run = async () => {
      const schemaClient = apiClients.schemaClient;
      // Each schema is an independent read; Promise.all keeps the requested order.
      const settled = await Promise.all(
        namesToFetch.map(async (systemName): Promise<[string, SchemaMetadata] | null> => {
          try {
            const response = await schemaClient.get('/schemas', {
              params: { systemName },
            });
            const schemaData = Array.isArray(response)
              ? response[0]
              : (response as { data?: unknown[] })?.data?.[0];

            const data = schemaData as
              | {
                  name?: string;
                  glyph?: string | null;
                  glyph_color?: GlyphColor | null;
                  description?: string | null;
                  schema_description_default?: string | null;
                }
              | undefined;

            const metadata: SchemaMetadata = {
              systemName,
              displayName: data?.name || formatSystemName(systemName),
              glyph: data?.glyph ?? null,
              glyphColor: data?.glyph_color ?? undefined,
              description:
                data?.description ?? data?.schema_description_default ?? null,
            };
            moduleCache.set(`${organizationId}::${systemName}`, metadata);
            return [systemName, metadata];
          } catch {
            // Leave the fallback in place; do not cache failures so retries can recover.
            return null;
          }
        }),
      );
      const fetchedEntries = settled.filter(
        (entry): entry is [string, SchemaMetadata] => entry !== null,
      );

      if (!cancelled && fetchedEntries.length > 0) {
        setResolved((prev) => {
          const next = new Map(prev);
          for (const [name, meta] of fetchedEntries) {
            next.set(name, meta);
          }
          return next;
        });
      }

      if (!cancelled) {
        setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
    // Depend on namesKey (value-equal projection of uniqueNames) rather than
    // uniqueNames itself: useMemo returns a new array reference each render,
    // which would refire the effect every render and infinite-loop with
    // setResolved.
  }, [apiClients?.schemaClient, organizationId, namesKey]);

  return { resolved, loading };
}

/**
 * Test-only helper for clearing the module cache between test cases.
 */
export function _resetSchemaMetadataCacheForTests(): void {
  moduleCache.clear();
}
